import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  CreditCard, Wallet, Smartphone, BanknoteIcon, ArrowLeft, ShieldCheck,
  Loader2, Copy, CheckCircle2, AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { StripeCardForm } from './stripe/StripeCardForm';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ETRANSFER_INSTRUCTIONS = {
  recipient: 'payments@creativebirthdays.ca',
  autoDeposit: 'Auto-deposit enabled — no security question needed',
};

export const PaymentMethodModal = ({
  open, onOpenChange, bookings = [], booking = null, onSuccess,
}) => {
  // Allow either a single booking or an array (e.g. from CrmCustomers "Take Payment")
  const bookingList = useMemo(() => booking ? [booking] : (bookings || []), [booking, bookings]);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [step, setStep] = useState('booking'); // 'booking' | 'methods' | 'card' | 'card_terminal' | 'paypal' | 'etransfer' | 'cash' | 'success'
  const [amount, setAmount] = useState('');
  const [pin, setPin] = useState('');
  const [note, setNote] = useState('');
  const [cashReceived, setCashReceived] = useState('');
  const [cardStarted, setCardStarted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [stripeConfig, setStripeConfig] = useState(null);
  const [paypalConfig, setPaypalConfig] = useState(null);
  const [success, setSuccess] = useState(null);

  // Card POS sub-state
  const [terminal, setTerminal] = useState(null);
  const [reader, setReader] = useState(null);
  const [cardStatus, setCardStatus] = useState(null);

  // E-transfer sub-state
  const [senderEmail, setSenderEmail] = useState('');
  const [referenceNote, setReferenceNote] = useState('');

  // Reset state when reopened — only fires on the open→true transition,
  // never on subsequent prop renders. (Bug fix iter-127: SSE refetches in the
  // parent created a new `bookingList` array reference every few seconds,
  // re-firing this effect and bouncing step back to 'methods' / 'booking' —
  // making it look like clicking Card/PayPal/E-Transfer/Cash "failed".)
  const initialisedRef = useRef(false);
  useEffect(() => {
    if (!open) {
      initialisedRef.current = false;
      return;
    }
    if (initialisedRef.current) return; // already initialised this open-session
    initialisedRef.current = true;

    const initialBooking = bookingList.length === 1 ? bookingList[0] : null;
    setSelectedBooking(initialBooking);
    setStep(bookingList.length === 1 ? 'methods' : 'booking');
    // Set initial amount from the booking's outstanding balance
    if (initialBooking) {
      const defaultAmount = (initialBooking.balance_due ?? initialBooking.outstanding ?? 0).toFixed(2);
      setAmount(String(defaultAmount));
    } else {
      setAmount('');
    }
    setPin('');
    setNote('');
    setCashReceived('');
    setCardStarted(false);
    setSuccess(null);
    setCardStatus(null);
    setSenderEmail('');
    setReferenceNote('');
    // Fetch capability flags
    Promise.all([
      axios.get(`${API_URL}/api/admin/stripe-terminal/config`, { withCredentials: true }).catch(() => ({ data: { configured: false } })),
      axios.get(`${API_URL}/api/payments/paypal/client-id`).catch(() => ({ data: { configured: false } })),
    ]).then(([s, p]) => {
      setStripeConfig(s.data);
      setPaypalConfig(p.data);
    });
  }, [open, bookingList]);

  // Set default amount when a different booking is selected (multi-booking mode)
  useEffect(() => {
    if (selectedBooking && bookingList.length > 1) {
      const defaultAmount = (selectedBooking.balance_due ?? selectedBooking.outstanding ?? 0).toFixed(2);
      setAmount(String(defaultAmount));
    }
  }, [selectedBooking?.booking_id, bookingList.length]);

  const close = () => {
    onOpenChange(false);
    // Best-effort terminal cleanup
    if (terminal && reader) {
      try { terminal.disconnectReader?.(); } catch { /* noop */ }
    }
    setTerminal(null);
    setReader(null);
  };

  // ── Step 1: pick a booking ─────────────────────────────────────────────
  const renderBookingPick = () => (
    <div className="space-y-2" data-testid="payment-step-booking">
      <p className="text-sm text-[#6B6B7A] mb-2">Select a booking to take payment for:</p>
      <div className="max-h-[55vh] overflow-y-auto space-y-2">
        {bookingList.map((b) => (
          <button
            key={b.booking_id}
            type="button"
            onClick={() => { setSelectedBooking(b); setStep('methods'); }}
            className="w-full text-left bg-white border border-[#E8E5DF] rounded-xl p-3 hover:border-[#FF3B6B] hover:bg-[#FFFDF8] transition"
            data-testid={`pick-booking-${b.booking_id}`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold text-sm text-[#1E1E24]">
                  {b.child_name} <span className="text-[#6B6B7A] font-normal">· {b.date} {b.time_slot && `at ${b.time_slot}`}</span>
                </p>
                <p className="text-xs text-[#6B6B7A]">
                  Outstanding: <strong className="text-[#FF3B6B]">${(b.balance_due ?? b.outstanding ?? 0).toFixed(2)}</strong>
                </p>
              </div>
              <Badge variant="outline" className="text-xs">{b.payment_status || 'unpaid'}</Badge>
            </div>
          </button>
        ))}
        {bookingList.length === 0 && (
          <p className="text-center text-sm text-[#6B6B7A] py-6">No bookings with outstanding balance.</p>
        )}
      </div>
    </div>
  );

  // ── Step 2: pick a method ──────────────────────────────────────────────
  const renderMethodPick = () => {
    const cardEnabled = stripeConfig?.configured;
    const paypalEnabled = paypalConfig?.configured;
    const methods = [
      { id: 'card', label: 'Credit / Debit Card', icon: CreditCard, color: '#FF3B6B', sub: cardEnabled ? 'Manual card entry (or tap reader)' : 'Card payments not configured', enabled: cardEnabled, testId: 'method-card' },
      { id: 'paypal', label: 'PayPal', icon: Smartphone, color: '#0070BA', sub: paypalEnabled ? (paypalConfig?.mode === 'live' ? 'PayPal live' : 'PayPal sandbox') : 'PayPal not configured', enabled: paypalEnabled, testId: 'method-paypal' },
      { id: 'etransfer', label: 'E-Transfer', icon: Wallet, color: '#06D6A0', sub: 'Interac e-Transfer · 2-step verify', enabled: true, testId: 'method-etransfer' },
      { id: 'cash', label: 'Cash', icon: BanknoteIcon, color: '#FFD166', sub: 'Record cash without external gateway', enabled: true, testId: 'method-cash' },
    ];
    return (
      <div className="space-y-3" data-testid="payment-step-methods">
        <div className="flex items-center justify-between bg-[#FFFDF8] border border-[#E8E5DF] rounded-xl p-3">
          <div>
            <p className="text-xs text-[#6B6B7A]">Booking</p>
            <p className="text-sm font-semibold text-[#1E1E24]">
              {selectedBooking?.child_name} · {selectedBooking?.date}
            </p>
          </div>
          <div className="text-right">
            <p className="text-xs text-[#6B6B7A]">Outstanding</p>
            <p className="text-base font-bold text-[#FF3B6B]">
              ${(selectedBooking?.balance_due ?? selectedBooking?.outstanding ?? 0).toFixed(2)}
            </p>
          </div>
        </div>

        <div>
          <Label className="text-xs">Amount to charge</Label>
          <Input
            type="number" step="0.01" min="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="rounded-xl border-[#E8E5DF] mt-1 text-base font-semibold"
            data-testid="payment-amount-input"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1">
          {methods.map((m) => (
            <button
              key={m.id}
              type="button"
              disabled={!m.enabled || !amount || Number(amount) <= 0}
              onClick={() => { if (m.id === 'card') setCardStarted(false); if (m.id === 'cash') setCashReceived(amount || ''); setStep(m.id); }}
              data-testid={m.testId}
              className={`text-left bg-white border-2 rounded-xl p-4 transition flex items-center gap-3 ${
                m.enabled ? 'border-[#E8E5DF] hover:border-[#FF3B6B] hover:shadow-sm' : 'border-[#F0EDE6] opacity-60 cursor-not-allowed'
              }`}
            >
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${m.color}20` }}>
                <m.icon className="w-5 h-5" style={{ color: m.color }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-[#1E1E24]">{m.label}</p>
                <p className="text-[11px] text-[#6B6B7A] truncate">{m.sub}</p>
              </div>
            </button>
          ))}
        </div>

        {bookingList.length > 1 && (
          <Button variant="ghost" size="sm" onClick={() => setStep('booking')} className="text-xs">
            <ArrowLeft className="w-3 h-3 mr-1" /> Pick a different booking
          </Button>
        )}
      </div>
    );
  };

  // ── Step 3a: Card POS via Stripe Terminal ──────────────────────────────
  const startCardPos = useCallback(async () => {
    if (!pin || pin.length !== 4) {
      toast.error('Enter your 4-digit PIN');
      return;
    }
    setBusy(true);
    setCardStatus('loading_sdk');
    try {
      // Lazy-load Stripe Terminal JS SDK from CDN
      if (!window.StripeTerminal) {
        await new Promise((resolve, reject) => {
          const tag = document.createElement('script');
          tag.src = 'https://js.stripe.com/terminal/v1/';
          tag.onload = resolve;
          tag.onerror = () => reject(new Error('Failed to load Stripe Terminal SDK'));
          document.head.appendChild(tag);
        });
      }
      // Init SDK
      const term = window.StripeTerminal.create({
        onFetchConnectionToken: async () => {
          const r = await axios.post(`${API_URL}/api/admin/stripe-terminal/connection-token`, {}, { withCredentials: true });
          return r.data.secret;
        },
        onUnexpectedReaderDisconnect: () => {
          setCardStatus('disconnected');
          toast.error('Reader disconnected');
        },
      });
      setTerminal(term);

      // Discover readers (simulated mode if no real reader registered)
      setCardStatus('discovering');
      const discoverConfig = stripeConfig?.simulated ? { simulated: true } : { simulated: false };
      const discover = await term.discoverReaders(discoverConfig);
      if (discover.error) throw new Error(discover.error.message);
      if (!discover.discoveredReaders.length) throw new Error('No readers found. Pair a reader in Stripe Dashboard or enable simulated mode.');
      setCardStatus('connecting');
      const conn = await term.connectReader(discover.discoveredReaders[0]);
      if (conn.error) throw new Error(conn.error.message);
      setReader(conn.reader);

      // Create payment intent on server
      setCardStatus('creating_intent');
      const intentRes = await axios.post(
        `${API_URL}/api/admin/stripe-terminal/payment-intent`,
        { booking_id: selectedBooking.booking_id, amount: Number(amount), pin },
        { withCredentials: true },
      );
      const { client_secret, payment_intent_id } = intentRes.data;

      // Reader collects payment
      setCardStatus('collecting');
      const collect = await term.collectPaymentMethod(client_secret);
      if (collect.error) throw new Error(collect.error.message);

      setCardStatus('processing');
      const proc = await term.processPayment(collect.paymentIntent);
      if (proc.error) throw new Error(proc.error.message);

      // Server-side finalize
      setCardStatus('finalizing');
      const final = await axios.post(
        `${API_URL}/api/admin/stripe-terminal/finalize`,
        { booking_id: selectedBooking.booking_id, payment_intent_id },
        { withCredentials: true },
      );
      setSuccess({ kind: 'card', amount: final.data.amount, fully_paid: final.data.fully_paid });
      setStep('success');
      toast.success(`$${final.data.amount?.toFixed(2)} charged via card terminal`);
      onSuccess?.();
    } catch (e) {
      setCardStatus('error');
      toast.error(`Card POS: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  }, [pin, selectedBooking, amount, stripeConfig, onSuccess]);

  // ── Step 3b: PayPal redirect ───────────────────────────────────────────
  const startPaypal = async () => {
    if (!pin || pin.length !== 4) { toast.error('Enter your 4-digit PIN'); return; }
    setBusy(true);
    try {
      const r = await axios.post(
        `${API_URL}/api/admin/manual-payments/paypal-link`,
        { booking_id: selectedBooking.booking_id, amount: Number(amount), pin },
        { withCredentials: true },
      ).catch(() => null);
      if (r?.data?.approval_url) {
        window.open(r.data.approval_url, '_blank', 'noopener,width=900,height=720');
        toast.info('PayPal opened in a new tab — complete the checkout, then click "Mark as completed" below.');
      } else {
        toast.error('PayPal endpoint not available — please use the customer-facing PayPal flow instead.');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'PayPal init failed');
    } finally {
      setBusy(false);
    }
  };

  // ── Step 3c: E-Transfer ────────────────────────────────────────────────
  const renderEtransferStep = () => (
    <div className="space-y-3" data-testid="payment-step-etransfer">
      <Button variant="ghost" size="sm" onClick={() => setStep('methods')} className="-ml-2 text-xs">
        <ArrowLeft className="w-3 h-3 mr-1" /> Back
      </Button>

      <div className="bg-[#FFFDF8] border-2 border-[#06D6A0]/40 rounded-xl p-4">
        <div className="flex items-start gap-2 mb-2">
          <Wallet className="w-5 h-5 text-[#06D6A0] flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-[#1E1E24]">Send Interac e-Transfer to:</h3>
            <p className="text-xs text-[#6B6B7A] mt-0.5">Read this to the customer. Confirm receipt before marking pending.</p>
          </div>
        </div>
        <div className="space-y-2 mt-3">
          <CopyRow label="Recipient" value={ETRANSFER_INSTRUCTIONS.recipient} />
          <CopyRow label="Amount" value={`$${Number(amount).toFixed(2)} CAD`} />
          <CopyRow label="Message" value={`Booking ${selectedBooking?.booking_id?.slice(0, 12) || ''}`} />
          <p className="text-[11px] text-[#6B6B7A] pt-1">
            <ShieldCheck className="w-3 h-3 inline mr-1 text-[#06D6A0]" />
            {ETRANSFER_INSTRUCTIONS.autoDeposit}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Customer's e-Transfer email (optional)</Label>
          <Input value={senderEmail} onChange={(e) => setSenderEmail(e.target.value)} placeholder="customer@example.com" className="rounded-xl border-[#E8E5DF] mt-1" data-testid="etransfer-sender-email" />
        </div>
        <div>
          <Label className="text-xs">Reference / note (optional)</Label>
          <Input value={referenceNote} onChange={(e) => setReferenceNote(e.target.value)} placeholder="e.g. Confirmation #" className="rounded-xl border-[#E8E5DF] mt-1" data-testid="etransfer-reference" />
        </div>
      </div>

      <PinInput pin={pin} onChange={setPin} testId="etransfer-pin" />

      <div className="bg-[#FFF7EB] border border-[#F5C15A] rounded-xl p-3 text-[11px] text-[#8A5A1F] flex gap-2">
        <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <span>Marked as <strong>pending</strong> until finance confirms the funds cleared in the bank. The booking balance won't update until then.</span>
      </div>

      <Button
        onClick={async () => {
          if (pin.length !== 4) { toast.error('Enter your 4-digit PIN'); return; }
          setBusy(true);
          try {
            const r = await axios.post(
              `${API_URL}/api/admin/payments/etransfer/initiate`,
              { booking_id: selectedBooking.booking_id, amount: Number(amount), pin, sender_email: senderEmail, reference_note: referenceNote },
              { withCredentials: true },
            );
            setSuccess({ kind: 'etransfer', amount: Number(amount), payment_id: r.data.payment_id });
            setStep('success');
            toast.success('Logged as pending — admin will confirm once funds clear');
            onSuccess?.();
          } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed');
          } finally { setBusy(false); }
        }}
        disabled={busy || pin.length !== 4 || !amount}
        className="w-full bg-[#06D6A0] hover:bg-[#05B88A] text-white rounded-full"
        data-testid="etransfer-mark-pending"
      >
        {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wallet className="w-4 h-4 mr-2" />}
        Mark as pending — verify later
      </Button>
    </div>
  );

  // ── Step 3d: Cash ──────────────────────────────────────────────────────
  const renderCashStep = () => {
    const amtNum = Number(amount) || 0;
    const cashNum = Number(cashReceived);
    const hasCash = cashReceived !== '' && !Number.isNaN(cashNum);
    const shortfall = hasCash && cashNum + 0.001 < amtNum;
    const changeDue = hasCash && cashNum >= amtNum ? cashNum - amtNum : 0;
    return (
      <div className="space-y-3" data-testid="payment-step-cash">
        <Button variant="ghost" size="sm" onClick={() => setStep('methods')} className="-ml-2 text-xs">
          <ArrowLeft className="w-3 h-3 mr-1" /> Back
        </Button>
        <div className="bg-[#FFFDF8] border-2 border-[#FFD166]/40 rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BanknoteIcon className="w-8 h-8 text-[#FFD166]" />
            <div>
              <p className="font-semibold text-[#1E1E24]">Record cash payment</p>
              <p className="text-xs text-[#6B6B7A]">Amount charged</p>
            </div>
          </div>
          <p className="text-xl font-bold text-[#1E1E24] tabular-nums" data-testid="cash-amount-charged">${amtNum.toFixed(2)}</p>
        </div>

        <div>
          <Label className="text-xs">Cash received from customer (CAD)</Label>
          <Input
            type="number" step="0.01" min="0"
            value={cashReceived}
            onChange={(e) => setCashReceived(e.target.value)}
            placeholder={amtNum.toFixed(2)}
            className="rounded-xl border-[#E8E5DF] mt-1 text-lg font-semibold"
            data-testid="cash-received-input"
          />
          {shortfall && (
            <p className="text-[11px] text-[#E5484D] mt-1" data-testid="cash-shortfall-error">
              Cash received is less than the ${amtNum.toFixed(2)} charged. To take a partial payment, go back and lower the amount to charge.
            </p>
          )}
        </div>

        <div className="flex items-center justify-between rounded-xl border border-[#E8E5DF] bg-white px-4 py-3" data-testid="cash-change-row">
          <span className="text-sm font-medium text-[#6B6B7A]">Change due to customer</span>
          <span className={`text-xl font-bold tabular-nums ${changeDue > 0 ? 'text-[#06D6A0]' : 'text-[#1E1E24]'}`} data-testid="cash-change-due">
            ${changeDue.toFixed(2)}
          </span>
        </div>

        <div>
          <Label className="text-xs">Notes (optional)</Label>
          <Textarea
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. Paid exact change · Customer owed $20 from last visit"
            className="rounded-xl border-[#E8E5DF] mt-1"
            data-testid="cash-note-input"
          />
        </div>

        <PinInput pin={pin} onChange={setPin} testId="cash-pin" />
        <Button
          onClick={async () => {
            if (pin.length !== 4) { toast.error('Enter your 4-digit PIN'); return; }
            if (shortfall) { toast.error('Cash received is less than the amount charged'); return; }
            setBusy(true);
            try {
              const r = await axios.post(
                `${API_URL}/api/admin/manual-payments`,
                {
                  booking_id: selectedBooking.booking_id,
                  amount: Number(amount),
                  pin,
                  payment_method: 'cash',
                  cash_received: hasCash ? cashNum : Number(amount),
                  note: note || undefined,
                },
                { withCredentials: true },
              );
              setSuccess({ kind: 'cash', amount: Number(amount), change_due: r.data.change_due, cash_received: r.data.cash_received, payment_id: r.data.manual_payment_id });
              setStep('success');
              toast.success(`$${Number(amount).toFixed(2)} cash recorded`);
              onSuccess?.();
            } catch (e) {
              toast.error(e.response?.data?.detail || 'Cash entry failed');
            } finally { setBusy(false); }
          }}
          disabled={busy || pin.length !== 4 || !amount || shortfall}
          className="w-full bg-[#FFD166] hover:bg-[#F0BC50] text-[#1E1E24] rounded-full font-bold"
          data-testid="cash-record-btn"
        >
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <BanknoteIcon className="w-4 h-4 mr-2" />}
          Record cash payment
        </Button>
      </div>
    );
  };

  // ── Step 3a render: Card (manual entry via Stripe Elements — default) ──
  const renderCardStep = () => {
    const amtNum = Number(amount) || 0;
    const outstanding = Number(selectedBooking?.balance_due ?? selectedBooking?.outstanding ?? 0) || 0;
    const overpay = amtNum > outstanding + 0.005;
    return (
      <div className="space-y-3" data-testid="payment-step-card">
        <Button variant="ghost" size="sm" onClick={() => { setCardStarted(false); setStep('methods'); }} className="-ml-2 text-xs">
          <ArrowLeft className="w-3 h-3 mr-1" /> Back
        </Button>
        <div className="bg-[#FFFDF8] border-2 border-[#FF3B6B]/30 rounded-xl p-4 flex items-center gap-3">
          <CreditCard className="w-8 h-8 text-[#FF3B6B]" />
          <div className="flex-1">
            <p className="font-semibold text-[#1E1E24]">Charge ${amtNum.toFixed(2)} — manual card entry</p>
            <p className="text-xs text-[#6B6B7A]">Key in the customer's card. They stay on this page — no redirect.</p>
          </div>
        </div>

        {overpay && (
          <div className="flex items-start gap-2 text-[11px] text-[#8A5A1F] bg-[#FFF7EB] border border-[#F5C15A] rounded-lg p-2" data-testid="card-overpay-warning">
            <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            <span>This exceeds the outstanding balance of ${outstanding.toFixed(2)}. The overpayment will be recorded as a credit.</span>
          </div>
        )}

        {!cardStarted ? (
          <>
            <div>
              <Label className="text-xs">Note (optional, internal)</Label>
              <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Phoned card payment"
                className="rounded-xl border-[#E8E5DF] mt-1" data-testid="card-note" />
            </div>
            <PinInput pin={pin} onChange={setPin} testId="card-pin" />
            <Button
              onClick={() => setCardStarted(true)}
              disabled={pin.length !== 4 || !amount || amtNum <= 0}
              className="w-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white rounded-full"
              data-testid="card-continue-btn"
            >
              <CreditCard className="w-4 h-4 mr-2" /> Continue to card entry
            </Button>
          </>
        ) : (
          <StripeCardForm
            context="admin"
            bookingId={selectedBooking?.booking_id}
            amount={amtNum}
            note={note}
            pin={pin}
            onCancel={() => setCardStarted(false)}
            onSuccess={(result) => {
              setSuccess({ kind: 'card', amount: result.amount, fully_paid: result.fully_paid });
              setStep('success');
              toast.success(`$${Number(result.amount).toFixed(2)} charged`);
              onSuccess?.();
            }}
          />
        )}

        <button
          type="button"
          onClick={() => setStep('card_terminal')}
          className="w-full text-xs text-[#6B6B7A] hover:text-[#FF3B6B] underline underline-offset-2"
          data-testid="use-terminal-link"
        >
          Use tap reader (Stripe Terminal) instead
        </button>
      </div>
    );
  };

  // ── Step 3a render: Card POS (Terminal — secondary option) ─────────────
  const renderCardTerminalStep = () => {
    const stages = {
      loading_sdk: 'Loading reader SDK…',
      discovering: 'Looking for readers…',
      connecting: 'Connecting to reader…',
      creating_intent: 'Creating payment…',
      collecting: 'Tap, insert or swipe card on the reader…',
      processing: 'Processing payment…',
      finalizing: 'Recording payment…',
      error: 'Something went wrong — try again.',
      disconnected: 'Reader disconnected',
    };
    return (
      <div className="space-y-3" data-testid="payment-step-card-terminal">
        <Button variant="ghost" size="sm" onClick={() => setStep('card')} className="-ml-2 text-xs">
          <ArrowLeft className="w-3 h-3 mr-1" /> Back to manual card entry
        </Button>
        <div className="bg-[#FFFDF8] border-2 border-[#FF3B6B]/30 rounded-xl p-4 flex items-center gap-3">
          <CreditCard className="w-8 h-8 text-[#FF3B6B]" />
          <div className="flex-1">
            <p className="font-semibold text-[#1E1E24]">Charge ${Number(amount).toFixed(2)} via card terminal</p>
            <p className="text-xs text-[#6B6B7A]">
              {stripeConfig?.simulated ? 'Simulated reader (Stripe dev mode) — no real card needed' : 'Reader will collect the customer\'s card'}
            </p>
          </div>
        </div>
        {cardStatus && (
          <div className="bg-white border border-[#E8E5DF] rounded-xl p-3 flex items-center gap-3 text-sm">
            {!['error', 'disconnected'].includes(cardStatus) && <Loader2 className="w-4 h-4 animate-spin text-[#FF3B6B]" />}
            {cardStatus === 'error' && <AlertCircle className="w-4 h-4 text-[#E5484D]" />}
            <span className="text-[#1E1E24]">{stages[cardStatus]}</span>
          </div>
        )}
        <PinInput pin={pin} onChange={setPin} testId="card-pin" />
        <Button
          onClick={startCardPos}
          disabled={busy || pin.length !== 4 || !amount}
          className="w-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white rounded-full"
          data-testid="card-charge-btn"
        >
          <CreditCard className="w-4 h-4 mr-2" /> Charge with terminal
        </Button>
      </div>
    );
  };

  // ── Step 3b render: PayPal ────────────────────────────────────────────
  const renderPaypalStep = () => (
    <div className="space-y-3" data-testid="payment-step-paypal">
      <Button variant="ghost" size="sm" onClick={() => setStep('methods')} className="-ml-2 text-xs">
        <ArrowLeft className="w-3 h-3 mr-1" /> Back
      </Button>
      <div className="bg-[#FFFDF8] border-2 border-[#0070BA]/30 rounded-xl p-4 flex items-center gap-3">
        <Smartphone className="w-8 h-8 text-[#0070BA]" />
        <div>
          <p className="font-semibold text-[#1E1E24]">Pay $${Number(amount).toFixed(2)} via PayPal</p>
          <p className="text-xs text-[#6B6B7A]">Opens PayPal checkout in a new tab. The customer can complete on their phone or your screen.</p>
        </div>
      </div>
      <PinInput pin={pin} onChange={setPin} testId="paypal-pin" />
      <Button
        onClick={startPaypal}
        disabled={busy || pin.length !== 4 || !amount || !paypalConfig?.configured}
        className="w-full bg-[#0070BA] hover:bg-[#005ea6] text-white rounded-full"
        data-testid="paypal-checkout-btn"
      >
        <Smartphone className="w-4 h-4 mr-2" /> Open PayPal checkout
      </Button>
      {!paypalConfig?.configured && (
        <p className="text-[11px] text-[#6B6B7A] text-center">PayPal credentials not yet configured. Add PAYPAL_CLIENT_ID & PAYPAL_SECRET to backend/.env.</p>
      )}
    </div>
  );

  // ── Step 4: success ────────────────────────────────────────────────────
  const renderSuccess = () => (
    <div className="text-center py-8 space-y-3" data-testid="payment-step-success">
      <div className="w-14 h-14 rounded-full bg-[#06D6A0]/15 flex items-center justify-center mx-auto">
        <CheckCircle2 className="w-8 h-8 text-[#06D6A0]" />
      </div>
      <h3 className="text-xl font-bold text-[#1E1E24]">
        {success?.kind === 'etransfer' ? 'Logged as pending' : `$${Number(success?.amount || 0).toFixed(2)} received`}
      </h3>
      {success?.kind === 'cash' && Number(success?.change_due) > 0 && (
        <p className="text-sm font-semibold text-[#06D6A0]" data-testid="cash-change-success">
          Change due to customer: ${Number(success.change_due).toFixed(2)}
        </p>
      )}
      <p className="text-sm text-[#6B6B7A]">
        {success?.kind === 'etransfer'
          ? 'Admin will confirm once the funds clear in the bank account.'
          : success?.fully_paid
            ? 'This booking is now fully paid 🎉'
            : 'Payment recorded successfully.'}
      </p>
      <Button onClick={close} className="bg-[#FF3B6B] hover:bg-[#E62E58] text-white rounded-full" data-testid="payment-done">Done</Button>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => o ? null : close()}>
      <DialogContent className="bg-white max-w-xl max-h-[92vh] overflow-y-auto" data-testid="payment-method-modal">
        <DialogHeader>
          <DialogTitle>
            {step === 'success' ? 'Payment recorded' : 'Take payment'}
          </DialogTitle>
          <DialogDescription>
            {step === 'methods' && 'Choose how the customer is paying.'}
            {step === 'booking' && 'Pick the booking you want to take payment for.'}
            {(step === 'card' || step === 'card_terminal' || step === 'paypal' || step === 'etransfer' || step === 'cash') && 'Enter your 4-digit PIN to authorize.'}
          </DialogDescription>
        </DialogHeader>

        {step === 'booking' && renderBookingPick()}
        {step === 'methods' && renderMethodPick()}
        {step === 'card' && renderCardStep()}
        {step === 'card_terminal' && renderCardTerminalStep()}
        {step === 'paypal' && renderPaypalStep()}
        {step === 'etransfer' && renderEtransferStep()}
        {step === 'cash' && renderCashStep()}
        {step === 'success' && renderSuccess()}
      </DialogContent>
    </Dialog>
  );
};

const PinInput = ({ pin, onChange, testId }) => (
  <div>
    <Label className="text-xs">Your 4-digit staff PIN</Label>
    <Input
      type="password"
      inputMode="numeric"
      maxLength={4}
      value={pin}
      onChange={(e) => onChange(e.target.value.replace(/\D/g, '').slice(0, 4))}
      placeholder="••••"
      className="rounded-xl border-[#E8E5DF] mt-1 text-center text-2xl tracking-[0.5em] font-bold"
      data-testid={testId}
    />
  </div>
);

const CopyRow = ({ label, value }) => {
  const [copied, setCopied] = useState(false);
  return (
    <div className="flex items-center justify-between bg-white border border-[#E8E5DF] rounded-lg px-3 py-2">
      <div>
        <p className="text-[10px] uppercase font-semibold text-[#6B6B7A] tracking-wider">{label}</p>
        <p className="text-sm font-mono text-[#1E1E24]">{value}</p>
      </div>
      <button
        type="button"
        onClick={() => {
          navigator.clipboard.writeText(value);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        }}
        className="text-xs text-[#FF3B6B] hover:bg-[#FF3B6B]/10 p-1.5 rounded-lg"
      >
        {copied ? <CheckCircle2 className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
      </button>
    </div>
  );
};

export default PaymentMethodModal;
